import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { fetchData } from 'utils';
import { useStore } from 'hooks/useStore';
import useSmartlook from 'hooks/useSmartlook';
import { handleErrorRedirect } from 'pages/Registration/BankId/BankInfo/useBankIdInfo';

export function useBankIDSessionsQuery(
  { uuid }: { uuid: string },
  enabled: boolean,
  refetchInterval?: number,
) {
  const navigate = useNavigate();
  const { error } = useSmartlook();
  const [storeBankId, setStoreBankId] = useStore((store) => store.bankId);

  return useQuery<Profile>({
    queryKey: ['proxy', 'bankid', 'sessions', { uuid }],
    queryFn: async () => {
      try {
        const res = await fetchData(`/proxy/bankid/sessions/${uuid}`);
        return res;
      } catch (e) {
        error('[useBankIDSessionsQuery][GET /proxy/bankid/sessions/{uuid}]', e);
        setStoreBankId({
          bankId: {
            ...storeBankId,
            error: true,
          },
        });
        handleErrorRedirect(navigate, 'session_error');
        return undefined;
      }
    },
    enabled,
    retry: 5,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 3000),
    refetchInterval,
    refetchIntervalInBackground: true,
  });
}

// This is basically replica of what's coming from BE, don't mind the camelCase and wrong translations.
export type Profile = {
  givenName: string;
  familyName: string;
  age: number;
  birthdate: string;
  birthnumber: string;
  cardNumber: string;
  address: string;
  addresses: Array<Address>;
  paymentAccounts: Array<string>;
  idcards: Array<Card>;
  identificationDocument: Doc;
  fullName: string;
  fullAge: string;
  nationality: string;
  sessionStatus:
    | 'ERROR'
    | 'SUCCESS'
    | 'PERSONAL_ID_MISMATCH'
    | 'NAME_SURNAME_MISMATCH'
    | 'INVALID_ACCOUNT_TYPE'
    | 'ID_CARD_DUPLICATED'
    | 'BANK_ACCOUNT_DUPLICATED'
    | 'NATIONALITY_BLACKLISTED'
    | 'FOREIGNER_NOT_ALLOWED'
    | 'BANKID_ERROR'
    | 'BANKID_DATA_MISMATCH'
    | 'BANKID_REJECTED_BY_CLIENT'
    | 'ACCESS_TOKEN_RECEIVED' // as pending status
    | undefined;
};

export type Address = {
  buildingapartment: string | null;
  street: string;
  streetnumber: string | null;
  city: string;
  zipcode: string;
  type: string;
};

export type Card = {
  country: string;
  number: string;
  description: string;
  type: 'ID' | 'P';
};

export type Doc = {
  country: string;
  issueDate: string;
  issuer: string;
  number: string;
  primaryIdentification: boolean;
  type:
    | 'ID_CARD'
    | 'PASSPORT'
    | 'RESIDENCE_PERMIT_BOOK'
    | 'RESIDENCE_PERMIT_CARD'
    | 'TRAVEL_DOCUMENT'
    | 'VISA_STICKER'
    | 'TEMPORARY_RESIDENCE_CERTIFICATE';
  validTo: string;
};
